if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent =
          this.querySelector(
            '[id^="QuickAddInfo-"]'
          );

        this.abortController = null;
        this.requestId = 0;

        this.addEventListener(
          'product-info:loaded',
          ({ target }) => {
            if (
              typeof target
                ?.addPreProcessCallback ===
              'function'
            ) {
              target.addPreProcessCallback(
                this.preprocessHTML.bind(this)
              );
            }
          }
        );
      }

      hide(preventFocus = false) {
        /*
         * Cancel any Quick Add product request
         * still running when the modal closes.
         */
        this.abortController?.abort();
        this.abortController = null;

        this.requestId += 1;

        const cartNotification =
          document.querySelector(
            'cart-notification'
          ) ||
          document.querySelector(
            'cart-drawer'
          );

        if (
          cartNotification &&
          typeof cartNotification
            .setActiveElement ===
            'function'
        ) {
          cartNotification.setActiveElement(
            this.openedBy
          );
        }

        if (this.modalContent) {
          this.modalContent.innerHTML = '';

          /*
           * Remove color scheme classes inherited
           * from the previously opened product.
           */
          Array.from(
            this.modalContent.classList
          ).forEach((className) => {
            if (
              className.startsWith(
                'color-'
              ) ||
              className === 'gradient'
            ) {
              this.modalContent.classList.remove(
                className
              );
            }
          });
        }

        if (preventFocus) {
          this.openedBy = null;
        }

        super.hide();
      }

      show(opener) {
        if (!opener) {
          return;
        }

        const productUrl =
          opener.getAttribute(
            'data-product-url'
          );

        if (!productUrl) {
          console.error(
            '[Quick Add] Product URL is missing.'
          );

          return;
        }

        /*
         * Cancel a previous Quick Add request.
         */
        this.abortController?.abort();

        this.abortController =
          new AbortController();

        const controller =
          this.abortController;

        const currentRequestId =
          ++this.requestId;

        const spinner =
          opener.querySelector(
            '.loading__spinner'
          );

        opener.setAttribute(
          'aria-disabled',
          'true'
        );

        opener.classList.add(
          'loading'
        );

        spinner?.classList.remove(
          'hidden'
        );

        fetch(productUrl, {
          method: 'GET',

          signal:
            controller.signal,

          headers: {
            Accept: 'text/html',
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(
                `Quick Add request failed: ${response.status} ${response.statusText}`
              );
            }

            return response.text();
          })
          .then((responseText) => {
            /*
             * Ignore a response belonging
             * to an older request.
             */
            if (
              controller.signal.aborted ||
              currentRequestId !==
                this.requestId
            ) {
              return;
            }

            const responseHTML =
              new DOMParser().parseFromString(
                responseText,
                'text/html'
              );

            const productElement =
              responseHTML.querySelector(
                'product-info'
              );

            if (!productElement) {
              throw new Error(
                'Quick Add product-info was not returned.'
              );
            }

            if (!this.modalContent) {
              throw new Error(
                'Quick Add modal content container was not found.'
              );
            }

            this.preprocessHTML(
              productElement
            );

            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              productElement.outerHTML
            );

            try {
              window.Shopify
                ?.PaymentButton
                ?.init?.();
            } catch (error) {
              console.warn(
                '[Quick Add] Payment button initialization failed:',
                error
              );
            }

            try {
              window.ProductModel
                ?.loadShopifyXR?.();
            } catch (error) {
              console.warn(
                '[Quick Add] Shopify XR initialization failed:',
                error
              );
            }

            /*
             * Verify the request is still current
             * immediately before opening.
             */
            if (
              currentRequestId ===
                this.requestId &&
              !controller.signal.aborted
            ) {
              super.show(opener);
            }
          })
          .catch((error) => {
            if (
              error?.name ===
                'AbortError'
            ) {
              return;
            }

            if (
              currentRequestId !==
              this.requestId
            ) {
              return;
            }

            console.error(
              '[Quick Add]',
              error
            );
          })
          .finally(() => {
            /*
             * An older request must not alter
             * loading UI belonging to a newer one.
             */
            if (
              currentRequestId !==
              this.requestId
            ) {
              return;
            }

            opener.removeAttribute(
              'aria-disabled'
            );

            opener.classList.remove(
              'loading'
            );

            spinner?.classList.add(
              'hidden'
            );
          });
      }

      preprocessHTML(productElement) {
        if (
          !productElement ||
          !this.modalContent
        ) {
          return;
        }

        /*
         * Clear product-specific color classes
         * from a previously opened product.
         */
        Array.from(
          this.modalContent.classList
        ).forEach((className) => {
          if (
            className.startsWith(
              'color-'
            ) ||
            className === 'gradient'
          ) {
            this.modalContent.classList.remove(
              className
            );
          }
        });

        productElement.classList.forEach(
          (classApplied) => {
            if (
              classApplied.startsWith(
                'color-'
              ) ||
              classApplied === 'gradient'
            ) {
              this.modalContent.classList.add(
                classApplied
              );
            }
          }
        );

        this.preventDuplicatedIDs(
          productElement
        );

        this.removeDOMElements(
          productElement
        );

        this.removeGalleryListSemantic(
          productElement
        );

        this.updateImageSizes(
          productElement
        );

        this.preventVariantURLSwitching(
          productElement
        );
      }

      preventVariantURLSwitching(
        productElement
      ) {
        if (!productElement) {
          return;
        }

        productElement.setAttribute(
          'data-update-url',
          'false'
        );
      }

      removeDOMElements(
        productElement
      ) {
        if (!productElement) {
          return;
        }

        productElement
          .querySelector(
            'pickup-availability'
          )
          ?.remove();

        productElement
          .querySelector(
            'product-modal'
          )
          ?.remove();

        productElement
          .querySelectorAll(
            'modal-dialog'
          )
          .forEach((modal) => {
            modal.remove();
          });
      }

      preventDuplicatedIDs(
        productElement
      ) {
        if (!productElement) {
          return;
        }

        const sectionId =
          productElement.dataset.section;

        if (!sectionId) {
          console.warn(
            '[Quick Add] Product section ID is missing.'
          );

          return;
        }

        const oldId =
          sectionId;

        const newId =
          `quickadd-${sectionId}`;

        /*
         * Dawn-generated product markup contains
         * the section ID in multiple IDs, labels,
         * form references and accessibility attrs.
         */
        productElement.innerHTML =
          productElement.innerHTML.replaceAll(
            oldId,
            newId
          );

        Array.from(
          productElement.attributes
        ).forEach((attribute) => {
          if (
            typeof attribute.value ===
              'string' &&
            attribute.value.includes(
              oldId
            )
          ) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replaceAll(
                oldId,
                newId
              )
            );
          }
        });

        /*
         * ProductInfo needs the original Shopify
         * section ID for AJAX section rendering.
         */
        productElement.dataset.originalSection =
          sectionId;
      }

      removeGalleryListSemantic(
        productElement
      ) {
        const galleryList =
          productElement?.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!galleryList) {
          return;
        }

        galleryList.setAttribute(
          'role',
          'presentation'
        );

        galleryList
          .querySelectorAll(
            '[id^="Slide-"]'
          )
          .forEach((item) => {
            item.setAttribute(
              'role',
              'presentation'
            );
          });
      }

      updateImageSizes(
        productElement
      ) {
        const product =
          productElement?.querySelector(
            '.product'
          );

        const desktopColumns =
          product?.classList.contains(
            'product--columns'
          );

        if (!desktopColumns) {
          return;
        }

        const mediaImages =
          product.querySelectorAll(
            '.product__media img'
          );

        if (!mediaImages.length) {
          return;
        }

        let mediaImageSizes =
          '(min-width: 1000px) 715px, ' +
          '(min-width: 750px) calc((100vw - 11.5rem) / 2), ' +
          'calc(100vw - 4rem)';

        if (
          product.classList.contains(
            'product--medium'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '605px'
            );
        } else if (
          product.classList.contains(
            'product--small'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '495px'
            );
        }

        mediaImages.forEach(
          (image) => {
            image.setAttribute(
              'sizes',
              mediaImageSizes
            );
          }
        );
      }
    }
  );
}